<script setup lang="ts">
import type { TicketSelectionMode } from '@/types/checkout';

const props = defineProps({
	isOpen: {
		type: Boolean,
		required: false,
		default: false,
	},
	eventId: {
		type: Number as PropType<number | null>,
		required: true,
	},
	isCreatingOrder: {
		type: Boolean,
		required: true,
		default: false,
	},
	selectionMode: {
		type: Object as PropType<TicketSelectionMode>,
		required: true,
		default: TICKET_SELECTION_MODE.TICKETS,
	},
});

const emit = defineEmits<{
	(e: 'onClose'): void
	(e: 'onContinueCheckout'): void
}>();

const checkoutStore = useCheckoutStore();
const eventStore = useEventStore();

const promoCode = ref();
const appliedPromoCode = ref();
const ticketSelectionFormRef = ref();

const eventId = computed(() => props.eventId);
const isLoading = computed(() => checkoutStore.ticketsInventoryResponse.isLoading);
const promoCodeServerErrorMessage = computed(() => checkoutStore.ticketPromotionsResponse.promotionErrorMessage);
const isUSEvent = computed(() => eventStore.isUSEvent);
const disabledPromoCode = computed(() => !!appliedPromoCode.value || props.isCreatingOrder || !isUSEvent.value);

const tickets = computed(() => {
	return checkoutStore.ticketsInventoryList.filter((item) => {
		if (props.selectionMode === TICKET_SELECTION_MODE.TICKETS) {
			return !item.requiresTokenGates;
		}
		return item.requiresTokenGates;
	});
});

function handleRemovePromoCode(isClearInternalStateOnly = false) {
	appliedPromoCode.value = '';
	promoCode.value = '';
	if (!isClearInternalStateOnly) {
		checkoutStore.cart.promoCode = '';
		checkoutStore.ticketPromotionsResponse.data = null;
	}
}

function handleClose() {
	handleRemovePromoCode();
	emit('onClose');
}

async function handleApplyPromoCode() {
	if (!promoCode.value || !eventId.value) {
		return;
	}

	await checkoutStore.getTicketPromotions(eventId.value, promoCode.value);
	if (checkoutStore.ticketPromotionsResponse.promotionErrorMessage) {
		return;
	}

	appliedPromoCode.value = promoCode.value;
	checkoutStore.cart.promoCode = appliedPromoCode.value;
	promoCode.value = '';
}

function handleCheckout() {
	const hasTicketOrdersError = ticketSelectionFormRef.value.hasTicketOrdersError();
	if (hasTicketOrdersError) {
		return;
	}
	emit('onContinueCheckout');
}

watch(
	() => props.isOpen,
	async (isOpen) => {
		if (isOpen && eventId.value) {
			await checkoutStore.getTicketsInventory(eventId.value);

			if (checkoutStore.cart.promoCode && checkoutStore.cart.orders.length) {
				// Set `appliedPromoCode` if there's existing promo code applied to cart
				appliedPromoCode.value = checkoutStore.cart.promoCode;
			} else if (checkoutStore.checkoutOrderResponse.data?.totalAmount && checkoutStore.cart.orders.length) {
				// Reset checkout order response when user back to ticket selection
				checkoutStore.checkoutOrderResponse.data = null;
			}
		} else if (!isOpen) {
			// Clear promo code's internal state
			handleRemovePromoCode(true);
		}
	},
);

watch(
	promoCode,
	(newPromoCode) => {
		if (!newPromoCode && checkoutStore.ticketPromotionsResponse.promotionErrorMessage) {
			// Remove the error message when user filled invalid code and remove the code from field.
			checkoutStore.ticketPromotionsResponse.promotionErrorMessage = '';
		}
	},
);
</script>
<template>
  <BaseSideDrawer
    :is-open="isOpen"
    @on-close="handleClose"
  >
    <template #header>
      <div class="check-out-header">
        <TicketIcon />
        <h1 class="text-xl text-semibold">
          Choose ticket
        </h1>
      </div>
    </template>
    <template #content>
      <div class="checkout-content">
        <BaseAlert v-if="!isUSEvent">
          <p class="text-sm text-regular">
            <span>
              To purchase tickets for events outside the United States, please follow these
            </span>
            <a
              href="https://help-attendee.peatix.com/en/support/solutions/articles/44002540319?_gl=1*nzmkfj*_gcl_au*NjIzNjYzMzkyLjE3Mjk4MDAyNTc"
              target="_blank"
              rel="noopener noreferrer"
            >steps</a>
            <span>.</span>
          </p>
        </BaseAlert>
        <TicketSelectionForm
          v-if="isOpen"
          ref="ticketSelectionFormRef"
          :tickets="tickets"
          :disabled="isCreatingOrder"
          :selection-mode="selectionMode"
        />
        <template v-if="!isLoading">
          <template v-if="selectionMode === TICKET_SELECTION_MODE.TICKETS">
            <Divider />
            <div
              class="promotion"
            >
              <BaseTextInput
                v-model="promoCode"
                label="Promo code"
                :disabled="disabledPromoCode"
                :error-message="promoCodeServerErrorMessage"
              >
                <template #append>
                  <BaseButton
                    class="btn-apply-code"
                    variant="subtle"
                    color="gray"
                    :disabled="disabledPromoCode"
                    @click="handleApplyPromoCode"
                  >
                    Apply
                  </BaseButton>
                </template>
              </BaseTextInput>
              <BaseChip
                v-if="appliedPromoCode"
                :size="CHIP_SIZE.LARGE"
                closable
                @on-close="handleRemovePromoCode"
              >
                {{ appliedPromoCode }}
              </BaseChip>
            </div>
          </template>
          <BaseAlert
            v-else
            :type="ALERT_TYPE.PRIMARY"
            class="alert-token-gate"
          >
            <span class="text-sm text-regular">
              Each order can include only one token gated ticket.
            </span>
          </BaseAlert>
          <Divider />
          <OrderSummaryItems :data="checkoutStore.orderSummary" />
        </template>
      </div>
    </template>
    <template #footer>
      <div class="checkout-footer">
        <ButtonSkeletonLoader
          v-if="isLoading"
          width="100"
          height="42"
        />
        <BaseButton
          v-else
          size="md"
          :disabled="isCreatingOrder || !isUSEvent"
          @click="handleCheckout"
        >
          Checkout
        </BaseButton>
      </div>
    </template>
  </BaseSideDrawer>
</template>
<style scoped lang="scss">
.check-out-header {
  display: flex;
  align-items: center;
  gap: spacings-get(2);
}

.checkout-content {
  display: flex;
  flex-direction: column;
  gap: spacings-get(6);
  padding-bottom: spacings-get(6);

  :deep(.text-medium) {
    color: colors-get(gray, 700);
  }

  :deep(.text-regular) {
    color: colors-get(gray, 600);
  }

  :deep(.--discount) {
    color: colors-get(error, 500);
  }

  :deep(.--not-valid) {
    color: colors-get(gray, 300);

    .text-medium, .text-regular {
      color: colors-get(gray, 300);
    }
  }

  .promotion {
    display: flex;
    flex-direction: column;
    gap: spacings-get(4);
  }

  .alert-token-gate .text-regular {
    color: inherit;
  }
}

.checkout-footer {
  display: flex;
  align-items: center;
  justify-content: end;
  gap: spacings-get(4);
  height: 100%;
  padding: 0 spacings-get(6);
  border-top: 1px solid #{colors-get(gray, 200)};
}
</style>
